import {useCallback, useEffect, useMemo, useState} from 'react'
import userApi from 'utils/api/users'
import storage from 'utils/storage'
import {getPalsconDashboardContexts} from './palsconApi'

const PALSCON_CONTEXT = 'palscon'

const isPalsconProfile = (profile) => (
  profile?.dashboardContext === PALSCON_CONTEXT &&
  profile?.dataSource === 'palsconregisteredusers' &&
  Boolean(profile?.palsconRegisteredUserId)
)

const mergePalsconDashboardProfile = (accountProfile, storedProfile) => {
  if (!isPalsconProfile(storedProfile)) return accountProfile
  if (String(accountProfile?._id || '') !== String(storedProfile?._id || '')) return accountProfile

  return {
    ...accountProfile,
    ...storedProfile,
    _id: accountProfile._id,
    accountRole: storedProfile.accountRole ?? accountProfile.role,
    role: 0,
  }
}

const withDashboardContexts = (profile, dashboardContexts) => ({
  ...profile,
  dashboardContexts,
})

export default function usePalsconDashboardContext(account) {
  const initialContexts = account?.dashboardContexts || null
  const [dashboardContexts, setDashboardContexts] = useState(initialContexts)
  const [isLoading, setIsLoading] = useState(false)
  const [isSwitching, setIsSwitching] = useState(false)

  useEffect(() => {
    if (account?.dashboardContexts) setDashboardContexts(account.dashboardContexts)
  }, [account?.dashboardContexts])

  useEffect(() => {
    if (!account?._id) return undefined
    let active = true
    setIsLoading(true)

    getPalsconDashboardContexts()
      .then((contexts) => {
        if (active) setDashboardContexts(contexts)
      })
      .catch((error) => {
        // The dashboard remains usable as the regular account if this optional lookup fails.
        console.warn('Unable to load the PALSCON dashboard toggle:', error.message)
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })

    return () => {
      active = false
    }
  }, [account?._id])

  const activeContext = account?.dashboardContext === PALSCON_CONTEXT ? PALSCON_CONTEXT : 'regular'
  const hasDashboardToggle = Boolean(
    dashboardContexts?.hasDashboardToggle &&
    dashboardContexts?.regular &&
    dashboardContexts?.palscon
  )

  const switchContext = useCallback(async (nextContext) => {
    if (isSwitching || nextContext === activeContext) return
    const nextProfile = dashboardContexts?.[nextContext]
    if (!nextProfile) return

    setIsSwitching(true)
    const storedProfile = withDashboardContexts(nextProfile, dashboardContexts)
    await storage.storeUser(storedProfile)
    userApi.seedUserCacheFromLoginPayload(storedProfile)

    const destination = Number(storedProfile.role) === 0 ? '/dashboard/app' : '/stores/app'
    window.location.assign(destination)
  }, [activeContext, dashboardContexts, isSwitching])

  return useMemo(() => ({
    activeContext,
    hasDashboardToggle,
    isLoading,
    isSwitching,
    switchContext,
  }), [activeContext, hasDashboardToggle, isLoading, isSwitching, switchContext])
}

export {isPalsconProfile, mergePalsconDashboardProfile}
